import React, {useEffect, useState, useCallback, useMemo} from 'react';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import ModalSimple from '../../../global/modal-simple';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/scrollbar';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import 'swiper/scss/zoom';
import {Keyboard, Scrollbar, Navigation, Zoom} from 'swiper/modules';
import {r} from '../../rendering/core';
import {prerender_set_pcs} from '../../rendering/web-only-render';
import * as emitter from '../../../global/emitter';
import {ProductCustomizationState} from '../../rendering/render.def';
import {ariaPulse, isNullOrWhiteSpace, modal, Portal, RenderType} from '../../../util';

declare const window: any;

const SliderTH: React.FC = observer(() => {
    const [zoomId, setZoomId] = useState<string>('');
    const [slides, setSlides] = useState<string[]>([]);
    const [slideViewCount, setSlideViewCount] = useState<number>(6);
    const [hasInit, setHasInit] = useState<boolean>(false);
    const [, forceUpdate] = useState({});
    const debug = true;

    const getSlidesPerView = useCallback(
        (value: number = 0) => {
            let count: number;
            if (window.innerWidth < 340) {
                count = 3;
            } else if (window.innerWidth < 400) {
                count = 3 + value;
            } else if (window.innerWidth < 510) {
                count = 4 + value;
            } else if (window.innerWidth < 580) {
                count = 5 + value;
            } else {
                count = 6 + value;
            }
            setSlideViewCount(count);
            if (debug) console.log('slideViewCount', count);
        },
        [debug],
    );

    const init = useCallback(() => {
        if (debug) console.log('1 hasInit', hasInit);
        if (!s || !s.prod) return;

        if (hasInit) {
            return;
        }

        const newSlides: string[] = [];
        if (!s.prod.th1Disabled) newSlides.push(s.prod.th1);
        if (!s.prod.th2Disabled) newSlides.push(s.prod.th2);
        if (!s.prod.th3Disabled) newSlides.push(s.prod.th3);
        if (!s.prod.th4Disabled) newSlides.push(s.prod.th4);
        if (!s.prod.th5Disabled) newSlides.push(s.prod.th5);
        if (!s.prod.th6Disabled) newSlides.push(s.prod.th6);
        if (!s.prod.th7Disabled) newSlides.push(s.prod.th7);
        if (!s.prod.th8Disabled) newSlides.push(s.prod.th8);
        if (!s.prod.th9Disabled) newSlides.push(s.prod.th9);
        if (!s.prod.th10Disabled) newSlides.push(s.prod.th10);

        setSlides(newSlides);
        setHasInit(true);
        if (debug) console.log('2 hasInit', true);

        getSlidesPerView();
        forceUpdate({});

        if (debug) console.log('slides.length', newSlides.length);
    }, [hasInit, debug, getSlidesPerView]);

    const showSlider = useMemo((): boolean => {
        if (!s.ready || !s.prod) {
            return false;
        }

        if (!slides || slides.length < 1) {
            if (debug) console.log('show_slider 1');
            if (!slides || slides.length < 1) {
                return false;
            }
            if (debug) console.log('show_slider 2');
            return true;
        }
        return true;
    }, [slides, debug]);

    const doLoop = useMemo((): boolean => {
        if (!s.ready) {
            return true;
        }

        if (slides.length <= 6) {
            return false;
        }

        return s.prod.CustomizationMethod >= 2;
    }, [slides.length]);

    const loadSwiper = useCallback(async (swiper: any) => {
        ariaPulse();
    }, []);

    const loadSwiperZoom = useCallback(
        async (swiper: any) => {
            if (!isNullOrWhiteSpace(zoomId)) {
                var ele = document.getElementById(zoomId);
                var index = parseInt(ele?.attributes['data-swiper-slide-index'].value, 10);
                swiper.slideToLoop(index);

                setTimeout(function () {
                    swiper.slideToLoop(index);
                }, 250);
                setTimeout(function () {
                    swiper.slideToLoop(index);
                }, 1250);
            }
            setZoomId('');
            ariaPulse();
        },
        [zoomId],
    );

    const toggleZoom = useCallback(async (e: any) => {
        setZoomId(e);
        prerender_set_pcs();
        let clone: ProductCustomizationState;
        try {
            clone = structuredClone(s.pcs);
        } catch (e) {
            clone = JSON.parse(JSON.stringify(s.pcs));
        }
        runInAction(() => {
            s.ps.css_zoom = r.render(clone, RenderType.Zoom);
            s.ui.zoom_thumbnails = true;
        });
        modal('modal-prod-swiper-th');
    }, []);

    useEffect(() => {
        const abForceUpdate = async () => forceUpdate({});
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', abForceUpdate);
        init();

        const handleModalClosed = (e: any) => {
            if (e.detail === 'modal-prod-swiper-th') {
                runInAction(() => {
                    s.ui.zoom_thumbnails = false;
                });
            }
        };
        document.addEventListener('modal-closed', handleModalClosed);

        return () => {
            emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
            document.removeEventListener('modal-closed', handleModalClosed);
        };
    }, [init]);

    useEffect(() => {
        getSlidesPerView();
    }, [getSlidesPerView]);

    useEffect(() => {
        if (!hasInit && s.ready && s.prod) {
            init();
        }
    }, [hasInit, init]);

    if (!s.ready) {
        if (debug) console.log('s not ready');
        return null;
    }
    if (!showSlider) {
        if (debug) console.log('showSlider', showSlider);
        return null;
    }

    if (!hasInit) {
        init();
    }

    if (!slides || slides.length < 1) {
        if (debug) console.log('slides null or empty');
        return null;
    }

    return (
        <div>
            <Swiper
                id="prod-swiper-th"
                onInit={loadSwiper}
                slidesPerView={slideViewCount}
                centeredSlidesBounds={!doLoop}
                centeredSlides={!doLoop}
                centerInsufficientSlides={!doLoop}
                grabCursor={doLoop}
                zoom={false}
                keyboard={{
                    enabled: doLoop,
                }}
                scrollbar={false}
                navigation={doLoop}
                height={75}
                loop={doLoop}
                pagination={false}
                modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                aria-roledescription="carousel"
                role="region"
                aria-label="Product images"
            >
                {s.prod.CustomizationMethod === 2 &&
                    slides.map((m, i) => (
                        <SwiperSlide
                            key={i}
                            onClick={() => toggleZoom(`slide-prod-real-${i}`)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleZoom(`slide-prod-real-${i}`);
                                }
                            }}
                            aria-label="Open larger image of this real life image"
                            tabIndex={0}
                            role="button"
                        >
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}${slides[i]}`}
                                />
                                <img
                                    alt="Real Life Image #1"
                                    src={`https://${Portal?.CDNDomain}${slides[i]}`}
                                    height={75}
                                    width="75"
                                />
                            </picture>
                        </SwiperSlide>
                    ))}
            </Swiper>
            {s.ui.zoom_thumbnails && (
                <ModalSimple
                    id="modal-prod-swiper-th"
                    title="Product Zoom"
                    open={true}
                    maxWidth={'90%'}
                    close={() => runInAction(() => (s.ui.zoom_thumbnails = false))}
                >
                    <Swiper
                        id="prod-swiper-th-zoom"
                        onInit={loadSwiperZoom}
                        slidesPerView={1}
                        centeredSlides={true}
                        grabCursor={true}
                        zoom={true}
                        keyboard={{enabled: true}}
                        loop={true}
                        scrollbar={true}
                        navigation={true}
                        pagination={false}
                        modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                        slidesPerGroupAuto={true}
                        aria-roledescription="carousel"
                        role="region"
                        aria-label="Product images"
                    >
                        {slides.map((m, i) => (
                            <SwiperSlide
                                key={i}
                                id={`slide-prod-real-${i}`}
                            >
                                <picture className="swiper-zoom-container">
                                    <source
                                        type="image/webp"
                                        srcSet={`https://${Portal?.CDNDomain}${slides[i]}`}
                                    />
                                    <img
                                        src={`https://${Portal?.CDNDomain}${slides[i]}`}
                                        alt="Real Image #1"
                                        style={{
                                            maxWidth: window.innerWidth * 0.8,
                                            maxHeight: window.innerHeight * 0.8,
                                        }}
                                        onClick={toggleZoom}
                                    />
                                </picture>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </ModalSimple>
            )}
        </div>
    );
});

export default SliderTH;
